import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { useChangeExamStatus, usePatchExam } from "src/hooks/useExam";
import { useCreateExam, useRemoveExams } from "src/hooks/useExams";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";
import CopyExamIcon from "src/images/Icons/newIcons/copy.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";
import ChangeNameIcon from "src/images/Icons/newIcons/edit.svg";
import GenerateExamIcon from "src/images/Icons/newIcons/fileClose.svg";
import FolderIcon from "src/images/Icons/newIcons/folder.svg";
import WatchIcon from "src/images/Icons/newIcons/play.svg";
import ResumeExamIcon from "src/images/Icons/newIcons/resume.svg";
import type { ActionItem } from "./types";
import type { ExamActionRow } from "./UnfinishedExamActions";

interface ExamActionsResult {
	actions: (ActionItem | false)[];
}

interface UseFinishedExamActionsParams {
	row: ExamActionRow;
	onWatch?: () => void;
	visibility?: { remove: boolean };
}

export const useFinishedExamActions = ({
	row,
	onWatch,
	visibility = { remove: true },
}: UseFinishedExamActionsParams): ExamActionsResult => {
	const { closeAllModals } = useModals();
	const history = useHistory();
	const { openModal } = useModals();
	const { t } = useTranslation();
	const { mutateAsync: generateExam } = useCreateExam() as unknown as {
		mutateAsync: (payload: Record<string, unknown>) => Promise<unknown>;
	};
	const { mutateAsync: removeExams } = useRemoveExams() as unknown as {
		mutateAsync: (ids: (string | number)[]) => Promise<void>;
	};
	const { mutateAsync: renameExam } = usePatchExam() as unknown as {
		mutateAsync: (payload: {
			id: string | number;
			name: string;
		}) => Promise<unknown>;
	};
	const { mutateAsync: patchExam } = useChangeExamStatus() as unknown as {
		mutateAsync: (payload: {
			id: string | number;
			status: string;
		}) => Promise<unknown>;
	};

	const generateExamFromWrongAnswers = async () => {
		closeAllModals();
		await generateExam({
			finishedExamsIds: [{ id: row.id }],
			additionalOption: {
				wrongAnswers: true,
			},
			name: `${t("defaultNames.wrongAnswers")} - ${dayjs(new Date()).format("DD-MM-YYYY")}`,
		});
	};

	const copyExam = async () => {
		closeAllModals();
		await generateExam({
			examsIds: [{ id: row.id }],
			name: row.name,
		});
	};

	const resumeExam = async () => {
		await patchExam({ id: row.id, status: "unfinished" });
		closeAllModals();
		return history.push(generatePath(routing.paths.exam, { id: row.id }));
	};

	return {
		actions: [
			{
				title: t("menuActions.exams.watch"),
				onClick: () => {
					history.push(
						generatePath(routing.paths.exam, {
							id: row.id,
							questionId: 1,
						}),
					);
					if (onWatch) {
						return onWatch();
					}
				},
				Icon: <WatchIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.copyExam"),
				testId: `exam-copy-${row.id}`,
				onClick: () => copyExam(),
				Icon: <CopyExamIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.changeName"),
				testId: `exam-change-name-${row.id}`,
				onClick: () =>
					openModal(ModalsNames.ChangeNameModal, {
						title: t("modals.changeName.exam.title", "Zmień nazwę egzaminu"),
						label: t("modals.changeName.exam.label", "Nazwa egzaminu"),
						placeholder: t(
							"modals.changeName.exam.placeholder",
							"Wpisz nazwę egzaminu",
						),
						name: row.name,
						onSave: (name: string) => renameExam({ id: row.id, name }),
					}),
				Icon: <ChangeNameIcon className="icon-small" />,
			},
			{
				title: t("menuActions.exams.statistics"),
				testId: `exam-statistics-${row.id}`,
				onClick: () => {
					openModal(ModalsNames.ExamStatisticsModal, {
						examId: row.id,
					});
				},
				Icon: <ChartIcon className="icon-small" />,
			},
			!row.allResolved && {
				title: t("menuActions.exams.resume"),
				onClick: () => resumeExam(),
				Icon: <ResumeExamIcon className="icon-small" />,
			},
			!!row.wrongAnswers && {
				title: t("menuActions.exams.generateExamFromWrongAnswers"),
				onClick: () => generateExamFromWrongAnswers(),
				Icon: <GenerateExamIcon className="icon-small" />,
			},
			!row.folderId && {
				title: t("menuActions.exams.moveToFolder", "Przenieś do folderu"),
				testId: `exam-move-to-folder-${row.id}`,
				onClick: () => {
					openModal(ModalsNames.MoveToFolderModal, {
						selectedItems: [{ id: row.id, itemId: row.id, itemType: "exam" }],
						isCopyMode: true,
						multiSelect: true,
						custom: {
							title: t("folder.moveToFolderTitle", "Przenieś do folderu"),
							actionLabel: t("folder.move", "Przenieś"),
						},
						onSuccess: () => {
							toast.success(
								t("folder.movedToFolderSuccess", "Przeniesiono do folderu"),
							);
						},
					});
				},
				Icon: <FolderIcon className="icon-small" />,
			},
			visibility.remove && {
				title: t("menuActions.exams.remove"),
				className: "list-item-error",
				testId: `exam-delete-${row.id}`,
				onClick: () => {
					openModal(ModalsNames.DeleteItemModal, {
						title: t("modals.deleteItem.exam.title", "Usunąć egzamin?"),
						description: t(
							"modals.deleteItem.exam.description",
							"Egzamin zostanie trwale usunięty razem z odpowiedziami i statystykami. Tej operacji nie można cofnąć.",
						),
						confirmText: t("modals.deleteItem.exam.confirm", "Usuń egzamin"),
						itemLabel: row.name,
						onDelete: () => removeExams([row.id]),
					});
				},
				Icon: <DeleteIcon className="icon-small" />,
			},
		],
	};
};
